import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parentAPI, schoolsAPI, gmailAPI, paymentAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Switch } from '../../components/ui/switch';
import {
  Settings, Globe, Clock, Mail, Save,
  CheckCircle, AlertTriangle, School, GraduationCap,
  Users, ChevronDown, ChevronUp, Link2, Unlink, RefreshCw,
  Plus, Trash2, Bell, Pill, CreditCard, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import SchoolGradeSelector from '../../components/SchoolGradeSelector';

const ROUTINE_CATEGORIES = ['activity', 'class', 'sport', 'art', 'music'];
const INTEREST_OPTIONS = [
  'animals', 'drawing', 'dinosaurs', 'music', 'dancing', 'outdoor',
  'water play', 'building', 'stories', 'science', 'cooking', 'nature',
  'art & craft', 'puzzles', 'trains', 'space', 'sports', 'numbers',
];
const ENERGY_OPTIONS = [
  { value: 'low',    label: 'Low',    desc: 'Calm, prefers quiet activities' },
  { value: 'medium', label: 'Medium', desc: 'Mix of active and calm' },
  { value: 'high',   label: 'High',   desc: 'Very active, loves to move' },
];
const EXPERIENCE_OPTIONS = [
  { value: 'new',        label: 'New',        desc: '0–1 year experience' },
  { value: 'mid',        label: 'Some',       desc: '2–4 years experience' },
  { value: 'experienced', label: 'Experienced', desc: '5+ years experience' },
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const defaultNewRoutine = () => ({
  id: `r_${Date.now()}`,
  name: '',
  category: 'activity',
  day_of_week: [0, 1, 2, 3, 4], // Mon-Fri by default
  start_time: '',
  duration_min: '',
  location: '',
  is_active: true,
});

const BillingSection = () => {
  const [sub, setSub] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    paymentAPI.getStatus()
      .then(({ data }) => setSub(data))
      .catch(() => setSub(null))
      .finally(() => setLoadingSub(false));
  }, []);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.')) return;
    setCancelling(true);
    try {
      await paymentAPI.cancel();
      toast.success('Subscription cancelled. It remains active until the end of this billing period.');
      setSub(prev => prev ? { ...prev, subscription_status: 'cancelled' } : prev);
    } catch (err) {
      toast.error('Failed to cancel subscription. Please contact support.');
    }
    setCancelling(false);
  };

  const status = sub?.subscription_status;
  const statusLabel = {
    active: 'Active',
    trial: 'Free trial',
    past_due: 'Payment due',
    cancelled: 'Cancelled',
  }[status] || 'No subscription';

  const statusColor = {
    active: 'text-green-600 bg-green-50',
    trial: 'text-blue-600 bg-blue-50',
    past_due: 'text-amber-600 bg-amber-50',
    cancelled: 'text-slate-500 bg-slate-50',
  }[status] || 'text-slate-500 bg-slate-50';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="billing-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
          <CreditCard className="w-4 h-4 text-primary" />
        </div>
        <h2 className="text-base font-semibold text-slate-800">Billing</h2>
      </div>

      {loadingSub ? (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading...
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Plan</span>
            <span className="text-sm font-medium text-slate-800">Kivo Family — Rs. 1,499/month</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Status</span>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
          </div>
          {sub?.subscription_ends_at && status === 'active' && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Next billing</span>
              <span className="text-sm text-slate-800">{new Date(sub.subscription_ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          {sub?.trial_ends_at && status === 'trial' && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Trial ends</span>
              <span className="text-sm text-slate-800">{new Date(sub.trial_ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          {(status === 'active' || status === 'trial') && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="mt-2 text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              {cancelling ? 'Cancelling...' : 'Cancel subscription'}
            </button>
          )}
          {(status === 'cancelled' || status === 'past_due' || !status) && (
            <a
              href="/subscribe"
              className="inline-block mt-2 text-sm text-primary hover:underline font-medium"
            >
              {status === 'cancelled' ? 'Resubscribe' : 'Subscribe now'}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Parent Settings Page - Minimal & Clean
 * Only essential settings: Children/Schools, Reminder Time, Caregiver Language, Gmail
 */
export const ParentSettings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlFamilyId = searchParams.get('family_id');
  const familyId = (urlFamilyId && urlFamilyId !== 'null') 
    ? urlFamilyId 
    : localStorage.getItem('family_id');
  
  const [settings, setSettings] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [reminderTime, setReminderTime] = useState('20:00');
  
  // Children/Schools state
  const [childrenWithSchools, setChildrenWithSchools] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [expandedChild, setExpandedChild] = useState(null);
  
  // Gmail OAuth state
  const [gmailConnections, setGmailConnections] = useState([]);
  const [loadingGmail, setLoadingGmail] = useState(false);
  const [connectingGmail, setConnectingGmail] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  
  // Child profile — { [childId]: { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' } }
  const [childProfiles, setChildProfiles] = useState({});
  const [savingProfile, setSavingProfile] = useState({});

  // Child pickup config — { [childId]: { pickupTime: 'HH:MM', isPickupEligible: bool } }
  const [childPickup, setChildPickup] = useState({});
  const [savingPickup, setSavingPickup] = useState({});

  // Caregiver experience — { [caregiverId]: 'new' | 'mid' | 'experienced' }
  const [caregiverExperience, setCaregiverExperience] = useState({});
  const [savingExperience, setSavingExperience] = useState({});

  // Caregiver-to-child assignments — { [caregiverId]: Set<childId> }
  const [caregiverAssignments, setCaregiverAssignments] = useState({});
  const [savingAssignments, setSavingAssignments] = useState({});

  // Routines state — { [childId]: RoutineItem[] }
  const [childRoutines, setChildRoutines] = useState({});
  const [savingRoutines, setSavingRoutines] = useState({});
  const [addingRoutineFor, setAddingRoutineFor] = useState(null);
  const [newRoutine, setNewRoutine] = useState(defaultNewRoutine());

  // Parent alert preferences — { [parentId]: { receive_critical_alerts, receive_school_alerts, receive_daily_updates } }
  const [parents, setParents] = useState([]);
  const [parentAlertPrefs, setParentAlertPrefs] = useState({});
  const [savingAlertPrefs, setSavingAlertPrefs] = useState({});

  // Family notification preferences
  const [notifPrefs, setNotifPrefs] = useState({
    parent_summary_time: '21:00',
    parent_whatsapp_verbosity: 'standard',
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  // Advanced settings toggle
  const [showAdvanced, setShowAdvanced] = useState(false);


  useEffect(() => {
    if (familyId) {
      loadSettings();
      loadChildrenSchools();
      loadGmailConnections();
      loadChildRoutines();
      loadFamilyParents();
      loadNotifPrefs();
    }
  }, [familyId]);
  
  // Handle Gmail OAuth callback params
  useEffect(() => {
    const gmailStatus = searchParams.get('gmail');
    const connectedEmail = searchParams.get('email');
    const errorReason = searchParams.get('reason');
    const warning = searchParams.get('warning');
    const returnedFamilyId = searchParams.get('family_id');
    
    const validFamilyId = returnedFamilyId && returnedFamilyId !== 'null' 
      ? returnedFamilyId 
      : (familyId && familyId !== 'null' ? familyId : null);
    
    if (gmailStatus === 'connected') {
      if (warning === 'needs_reauth') {
        toast.warning(`Gmail connected (${connectedEmail || 'Success!'}), but may need re-authorization later.`);
      } else {
        toast.success(`Gmail connected: ${connectedEmail || 'Success!'}`);
      }
      loadGmailConnections();
      if (validFamilyId) {
        window.history.replaceState({}, '', window.location.pathname + `?family_id=${validFamilyId}`);
      }
    }
    
    if (gmailStatus === 'error') {
      toast.error(`Gmail connection failed: ${decodeURIComponent(errorReason || 'Unknown error')}`);
      if (validFamilyId) {
        window.history.replaceState({}, '', window.location.pathname + `?family_id=${validFamilyId}`);
      }
    }
  }, [searchParams, familyId]);

  const loadChildRoutines = async () => {
    try {
      const response = await parentAPI.getToday(familyId);
      if (response.data?.ok && response.data?.children) {
        const routinesMap = {};
        const profilesMap = {};
        for (const child of response.data.children) {
          routinesMap[child.id] = child.routines || [];
          profilesMap[child.id] = child.profile || { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' };
        }
        setChildRoutines(routinesMap);
        setChildProfiles(profilesMap);
      }
    } catch (err) {
      console.error('Failed to load child routines/profiles:', err);
    }
  };

  const loadFamilyParents = async () => {
    try {
      const response = await parentAPI.getFamilyParents(familyId);
      if (response.data?.ok) {
        const ps = response.data.parents || [];
        setParents(ps);
        const prefsMap = {};
        for (const p of ps) {
          prefsMap[p.id] = {
            receive_critical_alerts: p.receive_critical_alerts ?? true,
            receive_school_alerts: p.receive_school_alerts ?? true,
            receive_daily_updates: p.receive_daily_updates ?? (p.is_primary_contact ? true : false),
          };
        }
        setParentAlertPrefs(prefsMap);
      }
    } catch (err) {
      console.error('Failed to load family parents:', err);
    }
  };

  const loadNotifPrefs = async () => {
    try {
      const response = await parentAPI.getFamilyPreferences(familyId);
      if (response.data?.ok && response.data?.preferences) {
        const prefs = response.data.preferences;
        setNotifPrefs(prev => ({
          ...prev,
          parent_summary_time: prefs.parent_summary_time || '21:00',
          parent_whatsapp_verbosity: prefs.parent_whatsapp_verbosity || 'standard',
        }));
      }
    } catch (err) {
      // Preferences may not exist yet — use defaults
      console.debug('Family preferences not loaded:', err);
    }
  };

  const handleSaveNotifPrefs = async () => {
    setSavingNotifPrefs(true);
    try {
      await parentAPI.updateFamilyPreferences(familyId, notifPrefs);
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
    } finally {
      setSavingNotifPrefs(false);
    }
  };


  const handleToggleAlertPref = async (parentId, field, value) => {
    // Optimistic update
    setParentAlertPrefs(prev => ({
      ...prev,
      [parentId]: { ...prev[parentId], [field]: value },
    }));
    setSavingAlertPrefs(prev => ({ ...prev, [parentId]: true }));
    try {
      await parentAPI.updateParentAlertPrefs(parentId, { [field]: value });
      toast.success('Alert preference saved');
    } catch (err) {
      // Rollback
      setParentAlertPrefs(prev => ({
        ...prev,
        [parentId]: { ...prev[parentId], [field]: !value },
      }));
      toast.error('Failed to save preference');
    } finally {
      setSavingAlertPrefs(prev => ({ ...prev, [parentId]: false }));
    }
  };

  const handleSaveChildProfile = async (childId) => {
    const profile = childProfiles[childId] || { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' };
    setSavingProfile(prev => ({ ...prev, [childId]: true }));
    try {
      await parentAPI.updateChildProfile(childId, profile);
      toast.success('Profile saved');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(prev => ({ ...prev, [childId]: false }));
    }
  };

  const toggleInterest = (childId, interest) => {
    setChildProfiles(prev => {
      const current = prev[childId] || { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' };
      const interests = current.interests.includes(interest)
        ? current.interests.filter(i => i !== interest)
        : [...current.interests, interest];
      return { ...prev, [childId]: { ...current, interests } };
    });
  };

  const setEnergyLevel = (childId, level) => {
    setChildProfiles(prev => {
      const current = prev[childId] || { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' };
      return { ...prev, [childId]: { ...current, energy_level: level } };
    });
  };

  const setProfileField = (childId, field, value) => {
    setChildProfiles(prev => {
      const current = prev[childId] || { interests: [], energy_level: 'medium', sensitivities: '', dietary_restrictions: '', parent_notes: '' };
      return { ...prev, [childId]: { ...current, [field]: value } };
    });
  };

  const handleSaveCaregiverExperience = async (caregiverId, level) => {
    setSavingExperience(prev => ({ ...prev, [caregiverId]: true }));
    setCaregiverExperience(prev => ({ ...prev, [caregiverId]: level }));
    try {
      await parentAPI.updateCaregiverExperience(caregiverId, level);
      toast.success('Experience level saved');
    } catch {
      toast.error('Failed to save experience level');
    } finally {
      setSavingExperience(prev => ({ ...prev, [caregiverId]: false }));
    }
  };

  const handleToggleCaregiverChild = (caregiverId, childId) => {
    setCaregiverAssignments(prev => {
      const current = new Set(prev[caregiverId] || []);
      if (current.has(childId)) current.delete(childId);
      else current.add(childId);
      return { ...prev, [caregiverId]: current };
    });
  };

  const handleSaveCaregiverAssignments = async (caregiverId) => {
    const assigned = [...(caregiverAssignments[caregiverId] || new Set())];
    setSavingAssignments(prev => ({ ...prev, [caregiverId]: true }));
    try {
      await parentAPI.assignCaregiverChildren(caregiverId, assigned);
      toast.success('Child assignment saved');
    } catch {
      toast.error('Failed to save assignment');
    } finally {
      setSavingAssignments(prev => ({ ...prev, [caregiverId]: false }));
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await parentAPI.getSettings(familyId);
      if (response.data?.ok) {
        setSettings(response.data.settings);
        const cgs = response.data.caregivers || [];
        setCaregivers(cgs);
        if (response.data.settings?.reminder_time) {
          setReminderTime(response.data.settings.reminder_time);
        }
        // Pre-populate caregiver experience
        const expMap = {};
        for (const cg of cgs) {
          if (cg.experience_level) expMap[cg.id] = cg.experience_level;
        }
        setCaregiverExperience(expMap);

        // Load existing caregiver-child assignments
        const assignMap = {};
        await Promise.all(cgs.map(async (cg) => {
          try {
            const r = await parentAPI.getCaregiverAssignedChildren(cg.id);
            if (r.data?.ok) {
              assignMap[cg.id] = new Set(r.data.child_ids || []);
            }
          } catch {
            assignMap[cg.id] = new Set();
          }
        }));
        setCaregiverAssignments(assignMap);
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadChildrenSchools = async () => {
    try {
      setLoadingChildren(true);
      const response = await schoolsAPI.getChildrenWithSchools(familyId);
      if (response.data?.ok) {
        const kids = response.data.children || [];
        setChildrenWithSchools(kids);
        // Seed pickup state from DB values
        const pickupInit = {};
        kids.forEach(c => {
          pickupInit[c.id] = {
            pickupTime: c.pickup_time ? c.pickup_time.slice(0, 5) : '',
            isPickupEligible: c.is_pickup_eligible ?? c.is_primary_school_child ?? false,
            transportMode: c.transport_mode || 'nanny_pickup',
          };
        });
        setChildPickup(pickupInit);
      }
    } catch (err) {
      console.error('Failed to load children schools:', err);
    } finally {
      setLoadingChildren(false);
    }
  };

  const loadGmailConnections = async () => {
    try {
      setLoadingGmail(true);
      const response = await gmailAPI.getConnections(familyId);
      if (response.data?.ok) {
        setGmailConnections(response.data.connections || []);
      }
    } catch (err) {
      console.error('Failed to load Gmail connections:', err);
    } finally {
      setLoadingGmail(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await parentAPI.updateSettings(familyId, { reminder_time: reminderTime });
      toast.success('Settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      setConnectingGmail(true);
      const response = await gmailAPI.startAuth(familyId);
      if (response.data?.ok && response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.error || 'Failed to start Gmail connection');
        setConnectingGmail(false);
      }
    } catch (err) {
      toast.error('Failed to connect Gmail');
      setConnectingGmail(false);
    }
  };

  const handleDisconnectGmail = async (connectionId) => {
    if (!confirm('Disconnect this Gmail account? School emails will no longer sync.')) return;
    
    try {
      setDisconnectingId(connectionId);
      const response = await gmailAPI.disconnect(connectionId, familyId);
      if (response.data?.ok) {
        toast.success('Gmail disconnected');
        loadGmailConnections();
      } else {
        toast.error(response.data?.error || 'Failed to disconnect');
      }
    } catch (err) {
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleSaveChildPickup = async (childId) => {
    const cfg = childPickup[childId] || {};
    setSavingPickup(prev => ({ ...prev, [childId]: true }));
    try {
      await parentAPI.updateChildPickup(childId, {
        pickupTime: cfg.pickupTime || null,
        isPickupEligible: cfg.isPickupEligible,
        transportMode: cfg.transportMode || 'nanny_pickup',
      });
    } catch (err) {
      console.error('Failed to save pickup config:', err);
    } finally {
      setSavingPickup(prev => ({ ...prev, [childId]: false }));
    }
  };

  const handleChildSchoolUpdate = async (childId, schoolDirectoryId, gradeKey) => {
    try {
      await schoolsAPI.updateChildSchool(childId, { 
        school_directory_id: schoolDirectoryId, 
        grade_key: gradeKey 
      });
      toast.success('School info updated');
      loadChildrenSchools();
    } catch (err) {
      toast.error('Failed to update school info');
    }
  };

  const handleAddRoutine = async (childId) => {
    if (!newRoutine.name.trim()) {
      toast.error('Routine name is required');
      return;
    }
    const current = childRoutines[childId] || [];
    const updated = [...current, { ...newRoutine, id: `r_${Date.now()}` }];
    await saveRoutines(childId, updated);
    setAddingRoutineFor(null);
    setNewRoutine(defaultNewRoutine());
  };

  const handleRemoveRoutine = async (childId, routineId) => {
    const current = childRoutines[childId] || [];
    const updated = current.filter(r => r.id !== routineId);
    await saveRoutines(childId, updated);
  };

  const saveRoutines = async (childId, routines) => {
    setSavingRoutines(prev => ({ ...prev, [childId]: true }));
    try {
      await parentAPI.updateChildRoutines(childId, routines);
      setChildRoutines(prev => ({ ...prev, [childId]: routines }));
      toast.success('Routines updated');
    } catch (err) {
      toast.error('Failed to save routines');
    } finally {
      setSavingRoutines(prev => ({ ...prev, [childId]: false }));
    }
  };

  const toggleRoutineDay = (dayIndex) => {
    setNewRoutine(prev => {
      const days = prev.day_of_week.includes(dayIndex)
        ? prev.day_of_week.filter(d => d !== dayIndex)
        : [...prev.day_of_week, dayIndex];
      return { ...prev, day_of_week: days };
    });
  };

  const handleCaregiverLanguageChange = async (caregiverId, language) => {
    try {
      await parentAPI.updateCaregiverLanguage(familyId, caregiverId, language);
      toast.success('Language preference updated');
      loadSettings();
    } catch (err) {
      toast.error('Failed to update language');
    }
  };

  if (!familyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">No Family Selected</h2>
        <Link to="/login" className="text-primary hover:underline">Please log in</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="parent-settings-page" className="max-w-3xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your family preferences</p>
      </div>

      {/* Children & Schools Card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="children-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Children & Schools</h2>
            <p className="text-sm text-slate-500">Configure school and grade for each child</p>
          </div>
        </div>
        
        {loadingChildren ? (
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mx-auto" />
          </div>
        ) : childrenWithSchools.length > 0 ? (
          <div className="space-y-3">
            {childrenWithSchools.map((child) => (
              <div key={child.id} className="border border-slate-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedChild(expandedChild === child.id ? null : child.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {child.first_name?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{child.first_name} {child.last_name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        {child.school_name && (
                          <span className="flex items-center gap-1">
                            <School className="w-3 h-3" />
                            {child.school_name}
                          </span>
                        )}
                        {child.grade_label && (
                          <span className="flex items-center gap-1">
                            <GraduationCap className="w-3 h-3" />
                            {child.grade_label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedChild === child.id ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedChild === child.id && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-6">
                    <SchoolGradeSelector
                      childId={child.id}
                      initialSchoolId={child.school_directory_id}
                      initialGradeKey={child.grade_key}
                      onUpdate={(schoolId, gradeKey) => handleChildSchoolUpdate(child.id, schoolId, gradeKey)}
                    />

                    {/* Pickup Configuration */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">Pickup & Check-ins</p>
                        <button
                          onClick={() => handleSaveChildPickup(child.id)}
                          disabled={savingPickup[child.id]}
                          className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg disabled:opacity-50"
                        >
                          {savingPickup[child.id] ? 'Saving…' : 'Save'}
                        </button>
                      </div>

                      {/* School-going toggle */}
                      <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl mb-3 cursor-pointer">
                        <div>
                          <p className="text-sm font-medium text-slate-800">School-going</p>
                          <p className="text-xs text-slate-500">Receives daily pickup check-ins from the nanny</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={childPickup[child.id]?.isPickupEligible ?? false}
                          onChange={e => setChildPickup(prev => ({
                            ...prev,
                            [child.id]: { ...prev[child.id], isPickupEligible: e.target.checked },
                          }))}
                          className="w-5 h-5 rounded accent-primary"
                        />
                      </label>

                      {/* Pickup time — only show when school-going */}
                      {childPickup[child.id]?.isPickupEligible && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <label className="text-sm text-slate-600 w-28 shrink-0">Pickup time</label>
                            <input
                              type="time"
                              value={childPickup[child.id]?.pickupTime || ''}
                              onChange={e => setChildPickup(prev => ({
                                ...prev,
                                [child.id]: { ...prev[child.id], pickupTime: e.target.value },
                              }))}
                              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800"
                            />
                            <p className="text-xs text-slate-400">IST</p>
                          </div>

                          {/* Transport mode */}
                          <div className="flex items-center gap-3">
                            <label className="text-sm text-slate-600 w-28 shrink-0">Transport</label>
                            <select
                              value={childPickup[child.id]?.transportMode || 'nanny_pickup'}
                              onChange={e => setChildPickup(prev => ({
                                ...prev,
                                [child.id]: { ...prev[child.id], transportMode: e.target.value },
                              }))}
                              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 bg-white"
                            >
                              <option value="nanny_pickup">Nanny picks up from school</option>
                              <option value="bus_drop">School bus drops home</option>
                              <option value="bus_stop_pickup">Nanny picks up at bus stop</option>
                              <option value="parent_pickup">Parent picks up</option>
                              <option value="no_pickup">Child comes home independently</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>


                    {/* About [Child] — profile intelligence inputs */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">About {child.first_name}</p>
                        <button
                          onClick={() => handleSaveChildProfile(child.id)}
                          disabled={savingProfile[child.id]}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-primary font-medium border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-50 transition-colors"
                        >
                          {savingProfile[child.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          Save
                        </button>
                      </div>

                      {/* Interests chip picker */}
                      <p className="text-xs text-slate-500 mb-2">Interests (select all that apply)</p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {INTEREST_OPTIONS.map(interest => {
                          const selected = (childProfiles[child.id]?.interests || []).includes(interest);
                          return (
                            <button
                              key={interest}
                              onClick={() => toggleInterest(child.id, interest)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                selected
                                  ? 'bg-primary text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {interest}
                            </button>
                          );
                        })}
                      </div>

                      {/* Energy level toggle */}
                      <p className="text-xs text-slate-500 mb-2">Energy level</p>
                      <div className="flex gap-2">
                        {ENERGY_OPTIONS.map(opt => {
                          const selected = (childProfiles[child.id]?.energy_level || 'medium') === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() => setEnergyLevel(child.id, opt.value)}
                              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors text-center ${
                                selected
                                  ? 'bg-primary text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              title={opt.desc}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {ENERGY_OPTIONS.find(o => o.value === (childProfiles[child.id]?.energy_level || 'medium'))?.desc}
                      </p>

                      {/* Sensitivities */}
                      <div className="mt-4">
                        <label className="text-xs text-slate-500 mb-1.5 block">Sensitivities</label>
                        <input
                          type="text"
                          value={childProfiles[child.id]?.sensitivities || ''}
                          onChange={(e) => setProfileField(child.id, 'sensitivities', e.target.value)}
                          placeholder="e.g. loud noises, crowded places, sudden changes"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                          maxLength={500}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Helps Kivo tailor activity suggestions and caregiver guidance</p>
                      </div>

                      {/* Dietary restrictions */}
                      <div className="mt-4">
                        <label className="text-xs text-slate-500 mb-1.5 block">Dietary restrictions</label>
                        <input
                          type="text"
                          value={childProfiles[child.id]?.dietary_restrictions || ''}
                          onChange={(e) => setProfileField(child.id, 'dietary_restrictions', e.target.value)}
                          placeholder="e.g. vegetarian, no nuts, egg-free"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                          maxLength={500}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Included in caregiver meal instructions</p>
                      </div>

                      {/* Parent notes */}
                      <div className="mt-4">
                        <label className="text-xs text-slate-500 mb-1.5 block">Parent notes</label>
                        <textarea
                          value={childProfiles[child.id]?.parent_notes || ''}
                          onChange={(e) => setProfileField(child.id, 'parent_notes', e.target.value)}
                          placeholder="Anything important Kivo or the caregiver should know"
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
                          maxLength={1000}
                        />
                      </div>
                    </div>

                    {/* Weekly Routines */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-slate-700">Weekly Routines</p>
                        {savingRoutines[child.id] && (
                          <RefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                        )}
                      </div>

                      {/* Existing routines */}
                      {(childRoutines[child.id] || []).length > 0 ? (
                        <div className="space-y-2 mb-3">
                          {(childRoutines[child.id] || []).map((routine) => (
                            <div key={routine.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800">{routine.name}</p>
                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                  <span className="text-xs text-slate-500 capitalize">{routine.category}</span>
                                  {routine.start_time && (
                                    <span className="text-xs text-slate-400">{routine.start_time}</span>
                                  )}
                                  {routine.duration_min && (
                                    <span className="text-xs text-slate-400">{routine.duration_min} min</span>
                                  )}
                                  <span className="text-xs text-slate-400">
                                    {routine.day_of_week?.map(d => DAY_LABELS[d]).join(', ')}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveRoutine(child.id, routine.id)}
                                disabled={savingRoutines[child.id]}
                                className="ml-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                                title="Remove routine"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        addingRoutineFor !== child.id && (
                          <p className="text-xs text-slate-400 mb-3">No routines added yet.</p>
                        )
                      )}

                      {/* Add routine form */}
                      {addingRoutineFor === child.id ? (
                        <div className="border border-primary-100 bg-primary-50 rounded-xl p-4 space-y-3">
                          <input
                            type="text"
                            placeholder="Routine name (e.g. Swimming class)"
                            value={newRoutine.name}
                            onChange={e => setNewRoutine(p => ({ ...p, name: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              value={newRoutine.category}
                              onChange={e => setNewRoutine(p => ({ ...p, category: e.target.value }))}
                              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                            >
                              {ROUTINE_CATEGORIES.map(c => (
                                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                              ))}
                            </select>
                            <input
                              type="time"
                              value={newRoutine.start_time}
                              onChange={e => setNewRoutine(p => ({ ...p, start_time: e.target.value }))}
                              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="number"
                              placeholder="Duration (min)"
                              value={newRoutine.duration_min}
                              onChange={e => setNewRoutine(p => ({ ...p, duration_min: e.target.value ? Number(e.target.value) : '' }))}
                              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                            />
                            <input
                              type="text"
                              placeholder="Location (optional)"
                              value={newRoutine.location}
                              onChange={e => setNewRoutine(p => ({ ...p, location: e.target.value }))}
                              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
                            />
                          </div>
                          {/* Day picker */}
                          <div>
                            <p className="text-xs text-slate-500 mb-1.5">Days</p>
                            <div className="flex gap-1 flex-wrap">
                              {DAY_LABELS.map((label, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => toggleRoutineDay(idx)}
                                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                                    newRoutine.day_of_week.includes(idx)
                                      ? 'bg-primary text-white'
                                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button
                              onClick={() => handleAddRoutine(child.id)}
                              disabled={savingRoutines[child.id]}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-60"
                            >
                              {savingRoutines[child.id] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              Save
                            </button>
                            <button
                              onClick={() => { setAddingRoutineFor(null); setNewRoutine(defaultNewRoutine()); }}
                              className="px-3 py-2 text-slate-500 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setAddingRoutineFor(child.id); setNewRoutine(defaultNewRoutine()); }}
                          className="flex items-center gap-1.5 px-3 py-2 text-primary text-sm font-medium border border-primary-100 rounded-lg hover:bg-primary-50 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add routine
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 bg-slate-50 rounded-xl text-center">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No children configured</p>
          </div>
        )}
      </div>

      {/* Medications & Classes — now managed in Care tab */}
      <Link
        to={`/parent/care?family_id=${familyId}`}
        className="block bg-white rounded-2xl border border-slate-100 p-5 hover:border-primary/30 hover:bg-primary-50/30 transition-all"
        data-testid="care-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
            <Pill className="w-5 h-5 text-rose-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-800">Medications & Classes</h3>
            <p className="text-xs text-slate-500">Manage medications, classes, and daily status in Care</p>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90" />
        </div>
      </Link>

      {/* Gmail Connection Card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="gmail-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Gmail Connection</h2>
            <p className="text-sm text-slate-500">Connect to receive school emails</p>
          </div>
        </div>

        {loadingGmail ? (
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mx-auto" />
          </div>
        ) : gmailConnections.length > 0 ? (
          <div className="space-y-3">
            {gmailConnections.map((conn) => (
              <div key={conn.id} className="p-4 bg-green-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{conn.email}</p>
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <span>{conn.status === 'needs_reauth' ? 'Needs re-authorization' : 'Connected'}</span>
                        {conn.watch_enabled && (
                          <>
                            <span className="text-green-400">•</span>
                            <span className="text-green-700 font-medium">Push enabled</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDisconnectGmail(conn.id)}
                    disabled={disconnectingId === conn.id}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
                  >
                    {disconnectingId === conn.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Disconnect
                  </button>
                </div>
                {conn.last_sync && (
                  <p className="mt-2 text-xs text-green-600/70 pl-8">
                    Last sync: {new Date(conn.last_sync).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <button
            onClick={handleConnectGmail}
            disabled={connectingGmail}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            {connectingGmail ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Link2 className="w-5 h-5" />
            )}
            Connect Gmail Account
          </button>
        )}
      </div>

      {/* Caregiver Language Card */}
      {caregivers.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="caregiver-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Caregiver Language</h2>
              <p className="text-sm text-slate-500">Preferred language for nanny messages</p>
            </div>
          </div>

          <div className="space-y-3">
            {caregivers.map((cg) => {
              const displayName = cg.full_name || cg.first_name || cg.name || cg.email || cg.phone || 'Unknown';
              const role = cg.role ? cg.role.charAt(0).toUpperCase() + cg.role.slice(1).toLowerCase() : null;
              return (
              <div key={cg.id} className="p-3 bg-slate-50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">
                      {role ? `${displayName} (${role})` : displayName}
                    </p>
                    {cg.email && cg.email !== displayName && (
                      <p className="text-xs text-slate-500">{cg.email}</p>
                    )}
                  </div>
                  <select
                    value={cg.preferred_language || 'en'}
                    onChange={(e) => handleCaregiverLanguageChange(cg.id, e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="hinglish">Hinglish</option>
                  </select>
                </div>
                {/* Experience level — affects activity instruction depth */}
                <div>
                  <p className="text-xs text-slate-500 mb-1.5">Experience level <span className="text-slate-400">(affects how detailed activity instructions are)</span></p>
                  <div className="flex gap-1.5">
                    {EXPERIENCE_OPTIONS.map(opt => {
                      const selected = (caregiverExperience[cg.id] || cg.experience_level || 'mid') === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleSaveCaregiverExperience(cg.id, opt.value)}
                          disabled={savingExperience[cg.id]}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            selected
                              ? 'bg-purple-600 text-white'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          } disabled:opacity-50`}
                          title={opt.desc}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Child assignment — which children this caregiver handles */}
                {childrenWithSchools.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs text-slate-500">Responsible for</p>
                      <button
                        onClick={() => handleSaveCaregiverAssignments(cg.id)}
                        disabled={savingAssignments[cg.id]}
                        className="text-xs px-2.5 py-1 bg-purple-600 text-white rounded-lg disabled:opacity-50"
                      >
                        {savingAssignments[cg.id] ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {childrenWithSchools.map(child => {
                        const assigned = (caregiverAssignments[cg.id] || new Set()).has(child.id);
                        return (
                          <label
                            key={child.id}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border transition-colors ${
                              assigned
                                ? 'bg-purple-100 border-purple-300 text-purple-800'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={assigned}
                              onChange={() => handleToggleCaregiverChild(cg.id, child.id)}
                            />
                            {child.first_name}
                          </label>
                        );
                      })}
                    </div>
                    {(caregiverAssignments[cg.id] || new Set()).size === 0 && (
                      <p className="text-xs text-slate-400 mt-1">No children assigned — handles all by default</p>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reminder Time Card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="reminder-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Daily Reminder</h2>
            <p className="text-sm text-slate-500">When to send task reminders</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <input
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-800 transition-colors"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* Notification Preferences Card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="notification-prefs-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Notification Preferences</h2>
              <p className="text-sm text-slate-500">Control when and how you receive updates</p>
            </div>
          </div>
          <button
            onClick={handleSaveNotifPrefs}
            disabled={savingNotifPrefs}
            className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg disabled:opacity-50"
          >
            {savingNotifPrefs ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="space-y-4">
          {/* Summary time */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-600 w-36 shrink-0">Evening summary</label>
            <input
              type="time"
              value={notifPrefs.parent_summary_time}
              onChange={e => setNotifPrefs(prev => ({ ...prev, parent_summary_time: e.target.value }))}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800"
            />
            <p className="text-xs text-slate-400">IST</p>
          </div>

          {/* Verbosity */}
          <div>
            <label className="text-sm text-slate-600 mb-2 block">WhatsApp update frequency</label>
            <div className="flex gap-2">
              {[
                { value: 'minimal', label: 'Minimal', desc: 'Health & safety only' },
                { value: 'standard', label: 'Standard', desc: 'Pickup + health + summary' },
                { value: 'all', label: 'All', desc: 'Every update immediately' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setNotifPrefs(prev => ({ ...prev, parent_whatsapp_verbosity: opt.value }))}
                  className={`flex-1 p-3 rounded-xl border text-left transition-colors ${
                    notifPrefs.parent_whatsapp_verbosity === opt.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs mt-0.5 opacity-70">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Parents & Alerts Card */}
      {parents.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="parents-alerts-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Parents & Alerts</h2>
              <p className="text-sm text-slate-500">Choose which alerts each parent receives</p>
            </div>
          </div>

          <div className="space-y-4">
            {parents.map((parent) => {
              const prefs = parentAlertPrefs[parent.id] || {
                receive_critical_alerts: true,
                receive_school_alerts: true,
                receive_daily_updates: parent.is_primary_contact,
              };
              const isSaving = savingAlertPrefs[parent.id];

              return (
                <div key={parent.id} className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-rose-600 text-sm font-semibold">
                        {(parent.full_name || 'P')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {parent.full_name || 'Parent'}
                        {parent.is_primary_contact && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-primary-100 text-primary rounded font-medium">Primary</span>
                        )}
                        {parent.id === user?.parentId && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">You</span>
                        )}
                      </p>
                      {parent.whatsapp_phone && (
                        <p className="text-xs text-slate-400">{parent.whatsapp_phone.replace('whatsapp:', '')}</p>
                      )}
                    </div>
                    {isSaving && <RefreshCw className="w-3.5 h-3.5 text-slate-400 animate-spin ml-auto" />}
                  </div>

                  <div className="space-y-2">
                    {/* Critical alerts — cannot be disabled for primary */}
                    <div className={`flex items-center justify-between p-2.5 rounded-lg ${
                      parent.is_primary_contact ? 'bg-slate-100 opacity-75' : 'bg-white'
                    }`}>
                      <div>
                        <p className="text-xs font-medium text-slate-700">Critical alerts</p>
                        <p className="text-[11px] text-slate-400">Health concerns, missed pickup, emergencies</p>
                      </div>
                      <Switch
                        checked={prefs.receive_critical_alerts}
                        disabled={parent.is_primary_contact || isSaving}
                        onCheckedChange={v => handleToggleAlertPref(parent.id, 'receive_critical_alerts', v)}
                      />
                    </div>
                    {parent.is_primary_contact && (
                      <p className="text-[10px] text-slate-400 px-2.5">Primary contact always receives critical alerts</p>
                    )}

                    {/* School alerts */}
                    <div className="flex items-center justify-between p-2.5 bg-white rounded-lg">
                      <div>
                        <p className="text-xs font-medium text-slate-700">School alerts</p>
                        <p className="text-[11px] text-slate-400">Urgent school emails, task reminders</p>
                      </div>
                      <Switch
                        checked={prefs.receive_school_alerts}
                        disabled={isSaving}
                        onCheckedChange={v => handleToggleAlertPref(parent.id, 'receive_school_alerts', v)}
                      />
                    </div>

                    {/* Daily updates */}
                    <div className="flex items-center justify-between p-2.5 bg-white rounded-lg">
                      <div>
                        <p className="text-xs font-medium text-slate-700">Daily updates</p>
                        <p className="text-[11px] text-slate-400">Morning briefing, evening summary at 9 PM</p>
                      </div>
                      <Switch
                        checked={prefs.receive_daily_updates ?? parent.is_primary_contact}
                        disabled={isSaving}
                        onCheckedChange={v => handleToggleAlertPref(parent.id, 'receive_daily_updates', v)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Billing & Subscription */}
      <BillingSection />

      {/* Advanced Settings Toggle */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
      >
        <Settings className="w-4 h-4" />
        {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
        {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {showAdvanced && (
        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
          <p className="text-sm text-slate-500 mb-4">
            Advanced settings are managed by Kivo support. Contact us for changes to:
          </p>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• Timezone configuration</li>
            <li>• School calendar sync</li>
            <li>• Multiple child support</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ParentSettings;
